import 'leaflet/dist/leaflet.css'
import './leaflet-marker-cluster-compat'
import 'leaflet.markercluster'

import { css } from '@emotion/react'
import { focusBoxShadow, Theme, useTheme } from 'bold-ui'
import Leaflet from 'leaflet'
import { PMTiles, Source } from 'pmtiles'
import { leafletLayer } from 'protomaps-leaflet'
import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { LeafletMapErrorBoundary } from './LeafletMapErrorBoundary'
import { BRASIL_MAP_CENTER, BRASIL_MAP_MAX_BOUNDS, LeafletLayerUrl, MAPA_TILES_URL, MapMarkerModel } from './map-model'
import { PAINT_RULES } from './map-style'
import { createObservedTileSource, TileSourceError } from './map-tile-source'
import { MapClusterMarker } from './MapClusterMarker'
import { MapFeedback } from './MapFeedback'
import { getClusterMarkerSize } from './utils-map'

interface LeafletMapProps {
  center?: [number, number]
  zoom?: number
  minZoom?: number
  maxBounds?: [[number, number], [number, number]]
  markers?: MapMarkerModel[]
  mapFeedback?: ReactNode
  clusterize?: boolean
  showLoading?: boolean
}

interface MarkerPortal {
  id: string
  container: HTMLElement
  content: ReactNode
}

const DEFAULT_MARKER_SIZE: [number, number] = [32, 41]
const FIT_MARKERS_PADDING: [number, number] = [48, 48]
const FIT_MARKERS_MAX_ZOOM = 16
const EMPTY_MARKERS: MapMarkerModel[] = []

export function LeafletMap(props: Readonly<LeafletMapProps>) {
  const {
    center = BRASIL_MAP_CENTER,
    zoom = 8,
    minZoom,
    maxBounds = BRASIL_MAP_MAX_BOUNDS,
    markers = EMPTY_MARKERS,
    mapFeedback,
    showLoading = false,
    clusterize = true,
  } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Leaflet.Map | null>(null)
  const markersLayerRef = useRef<Leaflet.LayerGroup | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isLoadingTiles, setIsLoadingTiles] = useState(false)
  const [tileError, setTileError] = useState<TileSourceError | null>(null)
  const [markerPortals, setMarkerPortals] = useState<MarkerPortal[]>([])

  const isMapLoading = isInitialLoading || isLoadingTiles || showLoading

  useEffect(() => {
    let isMounted = true

    const bounds = toLatLngBounds(maxBounds)
    const map = createMap(containerRef.current!, { center, zoom, bounds })
    mapRef.current = map
    markersLayerRef.current = createMarkersLayer(clusterize).addTo(map)

    const stopClampingMinZoom = clampMinZoomToBounds(map, bounds, minZoom)

    hideLeafletBranding(map)

    const tileSource = createObservedTileSource(MAPA_TILES_URL, (error) => {
      if (isMounted) setTileError(error)
    })
    const tileLayer = addBaseTileLayer(map, tileSource, theme.pallete.surface.background)

    const stopWatchingInitialLoad = watchInitialLoad(tileLayer, () => setIsInitialLoading(false))
    const stopWatchingTileLoading = watchTileLoading(tileLayer, setIsLoadingTiles)

    return () => {
      isMounted = false
      stopWatchingInitialLoad()
      stopWatchingTileLoading()
      stopClampingMinZoom()
      map.remove()
      mapRef.current = null
      markersLayerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const markersLayer = markersLayerRef.current
    if (!map || !markersLayer) {
      return
    }

    const contentByLayerId = new Map<number, ReactNode>()
    const leafletMarkers = markers.map((marker) => {
      const leafletMarker = createLeafletMarker(marker)
      contentByLayerId.set(Leaflet.stamp(leafletMarker), marker.content)
      return leafletMarker
    })

    const onLayerAdd = (event: Leaflet.LayerEvent) => handleMarkerLayerAdd(event, contentByLayerId, setMarkerPortals)
    const onLayerRemove = (event: Leaflet.LayerEvent) => handleMarkerLayerRemove(event, setMarkerPortals)

    map.on('layeradd', onLayerAdd)
    map.on('layerremove', onLayerRemove)

    markersLayer.clearLayers()
    addMarkersToLayer(markersLayer, leafletMarkers)

    if (markers.length > 0) {
      map.fitBounds(toMarkersBounds(markers), { padding: FIT_MARKERS_PADDING, maxZoom: FIT_MARKERS_MAX_ZOOM })
    }

    return () => {
      map.off('layeradd', onLayerAdd)
      map.off('layerremove', onLayerRemove)
      markersLayer.clearLayers()
      setMarkerPortals([])
    }
  }, [markers])

  return (
    <div css={styles.root}>
      <div ref={containerRef} css={styles.map} />
      {markerPortals.map(({ id, container, content }) => createPortal(content, container, id))}
      {!tileError && <MapFeedback isLoading={isMapLoading}>{mapFeedback}</MapFeedback>}
      <LeafletMapErrorBoundary error={tileError} />
    </div>
  )
}

function createMarkersLayer(clusterize: boolean): Leaflet.LayerGroup {
  if (!clusterize) {
    return Leaflet.layerGroup()
  }

  return Leaflet.markerClusterGroup({
    iconCreateFunction: (cluster) => {
      const size = getClusterMarkerSize(cluster.getChildCount())
      return Leaflet.divIcon({
        className: 'leaflet-map-cluster-marker',
        html: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      })
    },
    showCoverageOnHover: true,
    spiderfyOnMaxZoom: true,
  })
}

function isMarkerCluster(layer: Leaflet.Layer): layer is Leaflet.MarkerCluster {
  return layer instanceof Leaflet.MarkerCluster
}

function isMarkerClusterGroup(layer: Leaflet.LayerGroup): layer is Leaflet.MarkerClusterGroup {
  return layer instanceof Leaflet.MarkerClusterGroup
}

function addMarkersToLayer(markersLayer: Leaflet.LayerGroup, leafletMarkers: Leaflet.Marker[]): void {
  if (isMarkerClusterGroup(markersLayer)) {
    markersLayer.addLayers(leafletMarkers)
  } else {
    leafletMarkers.forEach((leafletMarker) => markersLayer.addLayer(leafletMarker))
  }
}

function upsertMarkerPortal(portals: MarkerPortal[], portal: MarkerPortal): MarkerPortal[] {
  return [...portals.filter((existing) => existing.id !== portal.id), portal]
}

function removeMarkerPortal(portals: MarkerPortal[], id: string): MarkerPortal[] {
  return portals.filter((portal) => portal.id !== id)
}

function handleMarkerLayerAdd(
  event: Leaflet.LayerEvent,
  contentByLayerId: Map<number, ReactNode>,
  setMarkerPortals: Dispatch<SetStateAction<MarkerPortal[]>>
): void {
  const layer = event.layer
  const element = ((layer as unknown) as Partial<Leaflet.Marker>).getElement?.()
  if (!element) {
    return
  }

  const content = isMarkerCluster(layer) ? (
    <MapClusterMarker count={layer.getChildCount()} />
  ) : (
    contentByLayerId.get(Leaflet.stamp(layer))
  )
  if (!content) {
    return
  }

  const id = String(Leaflet.stamp(layer))
  setMarkerPortals((prev) => upsertMarkerPortal(prev, { id, container: element, content }))
}

function handleMarkerLayerRemove(
  event: Leaflet.LayerEvent,
  setMarkerPortals: Dispatch<SetStateAction<MarkerPortal[]>>
): void {
  const id = String(Leaflet.stamp(event.layer))
  setMarkerPortals((prev) => removeMarkerPortal(prev, id))
}

function createLeafletMarker(marker: MapMarkerModel): Leaflet.Marker {
  const [width, height] = marker.size ?? DEFAULT_MARKER_SIZE
  const [anchorX, anchorY] = marker.anchor ?? [width / 2, height]
  const [lng, lat] = marker.coordinates

  const icon = Leaflet.divIcon({
    className: 'leaflet-map-marker',
    html: '',
    iconSize: [width, height],
    iconAnchor: [anchorX, anchorY],
  })

  return Leaflet.marker([lat, lng], { icon, keyboard: false })
}

function toMarkersBounds(markers: MapMarkerModel[]): Leaflet.LatLngBounds {
  return Leaflet.latLngBounds(markers.map(({ coordinates: [lng, lat] }) => [lat, lng] as [number, number]))
}

function toLatLngBounds(maxBounds: [[number, number], [number, number]]): Leaflet.LatLngBounds {
  return Leaflet.latLngBounds([
    [maxBounds[0][1], maxBounds[0][0]],
    [maxBounds[1][1], maxBounds[1][0]],
  ])
}

function createMap(
  container: HTMLDivElement,
  options: { center: [number, number]; zoom: number; bounds: Leaflet.LatLngBounds }
): Leaflet.Map {
  const [centerLng, centerLat] = options.center
  return Leaflet.map(container, {
    center: [centerLat, centerLng],
    zoom: options.zoom,
    maxZoom: 19,
    maxBounds: options.bounds,
  })
}

function hideLeafletBranding(map: Leaflet.Map): void {
  map.attributionControl.setPrefix(false)
}

function clampMinZoomToBounds(map: Leaflet.Map, bounds: Leaflet.LatLngBounds, minZoom?: number): () => void {
  const update = () => {
    const boundsMinZoom = map.getBoundsZoom(bounds, true)
    const targetMinZoom = minZoom !== undefined ? Math.max(minZoom, boundsMinZoom) : boundsMinZoom

    if (map.getZoom() < targetMinZoom) {
      map.setZoom(targetMinZoom, { animate: false })
    }

    map.setMinZoom(targetMinZoom)
  }
  update()
  map.on('resize', update)
  return () => map.off('resize', update)
}

function addBaseTileLayer(map: Leaflet.Map, tileSource: Source, backgroundColor: string): Leaflet.GridLayer {
  const layer = (leafletLayer({
    url: new PMTiles(tileSource) as LeafletLayerUrl,
    paintRules: PAINT_RULES,
    backgroundColor,
    attribution:
      '© Contribuidores do <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> © <a href="https://openmaptiles.org" target="_blank" rel="noopener">OpenMapTiles</a>',
    maxDataZoom: 14,
  }) as unknown) as Leaflet.GridLayer
  layer.addTo(map)
  return layer
}

function watchInitialLoad(layer: Leaflet.GridLayer, onInitialLoad: () => void): () => void {
  layer.once('load', onInitialLoad)
  return () => layer.off('load', onInitialLoad)
}

function watchTileLoading(layer: Leaflet.GridLayer, onLoadingChange: (isLoading: boolean) => void): () => void {
  const handleLoadStart = () => onLoadingChange(true)
  const handleLoadEnd = () => onLoadingChange(false)
  layer.on('loading', handleLoadStart)
  layer.on('load', handleLoadEnd)
  return () => {
    layer.off('loading', handleLoadStart)
    layer.off('load', handleLoadEnd)
  }
}

const createStyles = (theme: Theme) => ({
  root: css`
    position: relative;
    z-index: 0;
    width: 100%;
    height: 100%;
  `,
  map: css`
    width: 100%;
    height: 100%;
    outline: 0;
    &:focus {
      box-shadow: ${focusBoxShadow(theme)};
    }
  `,
})
